import { query } from 'bitecs';
import { Config } from '@src/core/Config';
import { difficulty } from '@src/core/Difficulty';
import { GameEvent } from '@src/core/EventQueue';
import { spawnFx, spawnProjectile, type ProjectileSpec } from '@src/ecs/archetypes';
import { Dying, Enemy, EnemyKind, Health, Player, Position, PrevPosition, Radius, Sprite, SpriteId, Team, Velocity } from '@src/ecs/components';
import { bodyRadius, enemyPairMinDist } from '@src/ecs/systems/CollisionSystem';
import type { GameSystem } from '@src/ecs/System';
import { emitParticles, ParticleKind, type GameContext } from '@src/ecs/world';
import { BOUNDS } from '@src/game/arena/RoomLayout';
import { floorMod } from '@src/game/FloorModifiers';
import { isWallEnemy } from '@src/game/WallEnemies';
import { WeaponId } from '@src/game/weapons/WeaponDefs';

const NEIGHBOR_CAP = 64;
const STEER_RATE = 9;
/** Gather pad past own body radius: covers the widest other body (boss 1.05) plus slop and crowdPad, and every steering range. */
const CROWD_GATHER = 1.2;
const COINCIDENT_EPS2 = 1e-8;

const IMP_CHASE = 0;
const IMP_FAKE = 1;
const IMP_WIND = 2;
const IMP_LUNGE = 3;

const GOLEM_WALK = 0;
const GOLEM_WIND = 1;
const GOLEM_STOMP = 2;

const BRUTE_WALK = 0;
const BRUTE_WIND = 1;
const BRUTE_CHARGE = 2;
const BRUTE_DAZED = 3;

const WRAITH_DRIFT = 0;
const WRAITH_FADE = 1;
const WRAITH_REFORM = 2;
const WRAITH_LUNGE = 3;

const HEXER_KITE = 0;
const HEXER_WIND = 1;

const MIMIC_DORMANT = 0;
const MIMIC_WAKING = 1;
const MIMIC_SQUAT = 2;
const MIMIC_HOP = 3;

const GOLEM_DUST = 0xc4b59a;
const BRUTE_EMBER = 0xff8c4a;
const WRAITH_MIST = 0x9ab4ff;
const HEXER_BOLT = 0x7ff0ff;
const SLIME_GOO = 0x6fe03a;
const BLIGHT_GOO = 0xb060ff;
const MIMIC_GOLD = 0xffd94a;

/**
 * Rebuilds the enemy spatial grid, then steers each kind: imps (peel + fake + lunge),
 * golems (walk then telegraph stomp), skulls (hover then committed lunge), slimes and
 * blights (phased squat then led hop), bats (own-radius orbit then offset dive), wraiths
 * (drift, fade, reform behind, lunge), hexers (kite and cast), cinders (ramping chase),
 * brutes (walk, lock, charge, daze) and mimics (sleep, wake, hop).
 * After steering, small fry drop the inward component along nearby bodies so next
 * movement cannot restack the overlap collision just opened. Runs before collision.
 */
export class EnemyAISystem implements GameSystem {
  private ctx!: GameContext;
  private readonly neighbors = new Int32Array(NEIGHBOR_CAP);
  private neighborCount = 0;
  private sepX = 0;
  private sepY = 0;
  private readonly hexBolt: ProjectileSpec = {
    owner: -1,
    weapon: WeaponId.Wand,
    sprite: SpriteId.BoltWand,
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    radius: Config.enemies.hexer.boltRadius,
    life: Config.enemies.hexer.boltLife,
    damage: Config.enemies.hexer.boltDamage,
    spawnerMult: 0,
    knockback: 2.2,
    pierce: 0,
    tint: HEXER_BOLT,
    scale: 0.8,
    grow: 0,
    team: Team.Enemy,
  };

  init(ctx: GameContext): void {
    this.ctx = ctx;
  }

  destroy(): void {}

  update(dt: number): void {
    const ctx = this.ctx;
    const grid = ctx.grid;
    const enemies = query(ctx.world, [Enemy, Position, Velocity, Radius]);

    grid.clear();
    for (let i = 0; i < enemies.length; i++) {
      const eid = enemies[i];
      if (Dying.flag[eid]) continue;
      grid.insert(eid, Position.x[eid], Position.y[eid]);
    }

    const players = query(ctx.world, [Player, Position, Velocity]);
    const steer = 1 - Math.exp(-STEER_RATE * dt);
    const t = ctx.time;
    // Haste floors: every walker, hopper and flyer wants to go a little faster.
    const haste = floorMod(ctx.floor.modifier).enemySpeed;

    for (let i = 0; i < enemies.length; i++) {
      const eid = enemies[i];
      if (Dying.flag[eid]) {
        // Corpses slide to a stop while the death clip plays.
        Velocity.x[eid] *= 0.85;
        Velocity.y[eid] *= 0.85;
        continue;
      }
      // BossSystem drives the boss; it is in the grid only so shots and bodies find it.
      if (Enemy.kind[eid] === EnemyKind.Boss) continue;
      const ex = Position.x[eid];
      const ey = Position.y[eid];

      // Nearest living player, plus their velocity for a short readable lead.
      let tx = 0;
      let ty = 0;
      let pvx = 0;
      let pvy = 0;
      let best = Infinity;
      for (let p = 0; p < players.length; p++) {
        const pid = players[p];
        if (Player.dead[pid]) continue;
        const dx = Position.x[pid] - ex;
        const dy = Position.y[pid] - ey;
        const d2 = dx * dx + dy * dy;
        if (d2 >= best) continue;
        best = d2;
        tx = dx;
        ty = dy;
        pvx = Velocity.x[pid];
        pvy = Velocity.y[pid];
      }
      if (best === Infinity) {
        Velocity.x[eid] *= 0.9;
        Velocity.y[eid] *= 0.9;
        continue;
      }
      const kind = Enemy.kind[eid] as EnemyKind;
      if (isWallEnemy(kind)) {
        Velocity.x[eid] = 0;
        Velocity.y[eid] = 0;
        continue;
      }
      if (kind === EnemyKind.Imp || kind === EnemyKind.Slime || kind === EnemyKind.Blight) {
        // Each walker owns a spot on a ring around the player instead of the same point.
        const spread = kind === EnemyKind.Imp ? Config.enemies.imp.spread : kind === EnemyKind.Slime ? Config.enemies.slime.spread : Config.enemies.blight.spread;
        const seedAng = Enemy.seed[eid] * Math.PI * 2;
        tx += Math.cos(seedAng) * spread;
        ty += Math.sin(seedAng) * spread;
      }
      const dist = Math.max(0.05, Math.hypot(tx, ty));
      let dx = tx / dist;
      let dy = ty / dist;

      const stagger = Enemy.stagger[eid];
      if (stagger > 0) Enemy.stagger[eid] = stagger - dt;
      // One grid query per small-fry body; separation and crowdSlide both read it. Heavies plow.
      const heavy = kind === EnemyKind.Golem || kind === EnemyKind.Brute;
      this.neighborCount = heavy ? 0 : grid.gather(ex, ey, bodyRadius(eid) + CROWD_GATHER, this.neighbors);

      let desiredX: number;
      let desiredY: number;
      let rate = steer;
      if (kind === EnemyKind.Brute) {
        // Walk in, lock a heading on the wizard, then commit to a charge until wall or contact.
        const c = Config.enemies.brute;
        Enemy.beat[eid] -= dt;
        if (stagger > 0 && Enemy.mode[eid] === BRUTE_WIND) {
          Enemy.mode[eid] = BRUTE_WALK;
          Enemy.beat[eid] = 0.5;
        }
        const mode = Enemy.mode[eid];
        if (mode === BRUTE_CHARGE) {
          desiredX = Enemy.lockX[eid] * c.chargeSpeed;
          desiredY = Enemy.lockY[eid] * c.chargeSpeed;
          rate = 1 - Math.exp(-20 * dt);
          const r = Radius.r[eid];
          const wall =
            (Enemy.lockX[eid] < -0.3 && ex <= BOUNDS.minX + r + 0.03) ||
            (Enemy.lockX[eid] > 0.3 && ex >= BOUNDS.maxX - r - 0.03) ||
            (Enemy.lockY[eid] < -0.3 && ey <= BOUNDS.minY + r + 0.03) ||
            (Enemy.lockY[eid] > 0.3 && ey >= BOUNDS.maxY - r - 0.03);
          if (wall) {
            Enemy.mode[eid] = BRUTE_DAZED;
            Enemy.beat[eid] = c.dazedSeconds;
            Velocity.x[eid] = -Enemy.lockX[eid] * 2;
            Velocity.y[eid] = -Enemy.lockY[eid] * 2;
            desiredX = 0;
            desiredY = 0;
            Sprite.squash[eid] = 0.6;
            Sprite.joltX[eid] = -Enemy.lockX[eid] * 0.4;
            Sprite.joltY[eid] = -Enemy.lockY[eid] * 0.4;
            spawnFx(ctx, SpriteId.Shockwave, ex, ey, 0, 2.2, GOLEM_DUST, 0.45);
            emitParticles(ctx.fx, ParticleKind.GolemRubble, ex, ey, -Enemy.lockX[eid], -Enemy.lockY[eid], 0.8);
            emitParticles(ctx.fx, ParticleKind.Dust, ex, ey - 0.4, 0, 1, 1.2, GOLEM_DUST);
            ctx.fx.shakeX += Enemy.lockX[eid] * 0.5;
            ctx.fx.shakeY += Enemy.lockY[eid] * 0.5 + 0.2;
            ctx.events.push(GameEvent.BruteWall, eid, 0, ex, ey);
            ctx.fx.rumble = Math.max(ctx.fx.rumble, 0.6);
          } else if (Enemy.beat[eid] <= 0) {
            Enemy.mode[eid] = BRUTE_WALK;
            Enemy.beat[eid] = c.chargeRest * (0.8 + Enemy.seed[eid] * 0.4);
          } else if (ctx.rng.chance(dt * 16)) {
            emitParticles(ctx.fx, ParticleKind.Embers, ex - Enemy.lockX[eid] * 0.4, ey - 0.3, -Enemy.lockX[eid], -Enemy.lockY[eid], 0.5, BRUTE_EMBER);
          }
        } else if (mode === BRUTE_DAZED) {
          desiredX = 0;
          desiredY = 0;
          Sprite.rot[eid] = Math.sin(t * 9 + Enemy.seed[eid] * 4) * 0.14;
          if (Enemy.beat[eid] <= 0) {
            Enemy.mode[eid] = BRUTE_WALK;
            Enemy.beat[eid] = 0.4;
          }
        } else if (mode === BRUTE_WIND) {
          desiredX = 0;
          desiredY = 0;
          rate = 1 - Math.exp(-14 * dt);
          Sprite.squash[eid] = -0.5;
          Sprite.flash[eid] = Math.max(Sprite.flash[eid], 0.4);
          // Track the wizard for most of the windup, then the heading locks: the tell is honest.
          if (Enemy.beat[eid] > c.chargeWind * 0.35) {
            const lx = tx + pvx * c.lead;
            const ly = ty + pvy * c.lead;
            const llen = Math.max(0.05, Math.hypot(lx, ly));
            Enemy.lockX[eid] = lx / llen;
            Enemy.lockY[eid] = ly / llen;
            if (Math.abs(Enemy.lockX[eid]) > 0.2) Sprite.face[eid] = Enemy.lockX[eid] < 0 ? -1 : 1;
          }
          if (Enemy.beat[eid] <= 0) {
            Enemy.mode[eid] = BRUTE_CHARGE;
            Enemy.beat[eid] = c.chargeSeconds;
            Velocity.x[eid] = Enemy.lockX[eid] * c.chargeSpeed;
            Velocity.y[eid] = Enemy.lockY[eid] * c.chargeSpeed;
            Sprite.squash[eid] = 0.5;
            spawnFx(ctx, SpriteId.Shockwave, ex, ey, 0, 1.6, BRUTE_EMBER, 0.45);
            emitParticles(ctx.fx, ParticleKind.Dust, ex, ey - 0.4, -Enemy.lockX[eid], -Enemy.lockY[eid], 1.2, GOLEM_DUST);
            ctx.fx.shakeY += 0.12;
          }
        } else {
          const speed = c.speed * (stagger > 0 ? Config.anim.golemStaggerSpeed : 1);
          desiredX = dx * speed;
          desiredY = dy * speed;
          if (stagger <= 0 && Enemy.beat[eid] <= 0 && dist < c.chargeRange && dist > 2.2) {
            Enemy.mode[eid] = BRUTE_WIND;
            Enemy.beat[eid] = c.chargeWind;
            Enemy.lockX[eid] = dx;
            Enemy.lockY[eid] = dy;
            Sprite.flash[eid] = 0.6;
            Sprite.squash[eid] = -0.4;
            spawnFx(ctx, SpriteId.Shockwave, ex, ey, 0, 1.1, BRUTE_EMBER, 0.4);
          } else if (Enemy.beat[eid] <= 0) {
            Enemy.beat[eid] = 0.25;
          }
          Sprite.rot[eid] = Math.sin(t * 5 + Enemy.seed[eid] * 10) * 0.07;
        }
      } else if (kind === EnemyKind.Wraith) {
        // Drift, fade out (untouchable), reform a few paces behind the wizard, lunge.
        const c = Config.enemies.wraith;
        Enemy.beat[eid] -= dt;
        const mode = Enemy.mode[eid];
        if (mode === WRAITH_FADE) {
          desiredX = 0;
          desiredY = 0;
          Health.invuln[eid] = 0.05;
          const u = 1 - Math.max(0, Enemy.beat[eid]) / c.fadeSeconds;
          Sprite.alpha[eid] = 0.9 - u * 0.8;
          if (Enemy.beat[eid] <= 0) {
            // Behind: continue past the wizard along the approach line, with a sideways jitter.
            const jit = (Enemy.seed[eid] - 0.5) * 1.8;
            let nx = ex + tx + dx * c.flankRange - dy * jit;
            let ny = ey + ty + dy * c.flankRange + dx * jit;
            const r = Radius.r[eid] + 0.3;
            nx = Math.max(BOUNDS.minX + r, Math.min(BOUNDS.maxX - r, nx));
            ny = Math.max(BOUNDS.minY + r, Math.min(BOUNDS.maxY - r, ny));
            spawnFx(ctx, SpriteId.Smoke, ex, ey, ctx.rng.next() * Math.PI * 2, 1.1, WRAITH_MIST, 0.5);
            Position.x[eid] = nx;
            Position.y[eid] = ny;
            PrevPosition.x[eid] = nx;
            PrevPosition.y[eid] = ny;
            Velocity.x[eid] = 0;
            Velocity.y[eid] = 0;
            Enemy.mode[eid] = WRAITH_REFORM;
            Enemy.beat[eid] = c.reformSeconds;
            Sprite.alpha[eid] = 0.1;
            emitParticles(ctx.fx, ParticleKind.PortalMotes, nx, ny, 0, 1, 0.6, WRAITH_MIST);
          }
        } else if (mode === WRAITH_REFORM) {
          desiredX = 0;
          desiredY = 0;
          Health.invuln[eid] = 0.05;
          const u = 1 - Math.max(0, Enemy.beat[eid]) / c.reformSeconds;
          Sprite.alpha[eid] = 0.1 + u * 0.9;
          Sprite.flash[eid] = Math.max(Sprite.flash[eid], u * 0.5);
          if (Math.abs(tx) > 0.2) Sprite.face[eid] = tx < 0 ? -1 : 1;
          if (Enemy.beat[eid] <= 0) {
            Enemy.mode[eid] = WRAITH_LUNGE;
            Enemy.beat[eid] = c.lungeSeconds;
            Health.invuln[eid] = 0;
            Velocity.x[eid] = dx * c.lungeSpeed;
            Velocity.y[eid] = dy * c.lungeSpeed;
            Sprite.alpha[eid] = 1;
            Sprite.squash[eid] = -0.5;
          }
        } else if (mode === WRAITH_LUNGE) {
          desiredX = Velocity.x[eid];
          desiredY = Velocity.y[eid];
          if (Enemy.beat[eid] <= 0) {
            Enemy.mode[eid] = WRAITH_DRIFT;
            Enemy.beat[eid] = c.driftSeconds * (0.7 + Enemy.seed[eid] * 0.6);
          }
        } else {
          Sprite.alpha[eid] = Math.min(0.9, Sprite.alpha[eid] + dt * 2);
          const wobble = Math.sin(t * 2.2 + Enemy.seed[eid] * 20) * 0.7;
          const speed = c.speed * (stagger > 0 ? 0.4 : 1);
          desiredX = (dx - dy * wobble) * speed;
          desiredY = (dy + dx * wobble) * speed;
          if (stagger <= 0 && Enemy.beat[eid] <= 0 && dist > 1.6) {
            Enemy.mode[eid] = WRAITH_FADE;
            Enemy.beat[eid] = c.fadeSeconds;
            emitParticles(ctx.fx, ParticleKind.PortalMotes, ex, ey, 0, 1, 0.4, WRAITH_MIST);
          }
        }
        Sprite.rot[eid] = Math.sin(t * 2.5 + Enemy.seed[eid] * 10) * 0.15;
      } else if (kind === EnemyKind.Hexer) {
        // Hold a band around kiteRange, strafe, then plant and cast an aimed hex bolt.
        const c = Config.enemies.hexer;
        Enemy.beat[eid] -= dt;
        if (stagger > 0 && Enemy.mode[eid] === HEXER_WIND) {
          Enemy.mode[eid] = HEXER_KITE;
          Enemy.beat[eid] = 0.6;
        }
        if (Enemy.mode[eid] === HEXER_WIND) {
          desiredX = 0;
          desiredY = 0;
          rate = 1 - Math.exp(-12 * dt);
          const u = 1 - Math.max(0, Enemy.beat[eid]) / c.telegraph;
          Sprite.flash[eid] = Math.max(Sprite.flash[eid], 0.3 + u * 0.5);
          Sprite.squash[eid] = -0.35 - u * 0.2;
          if (Math.abs(tx) > 0.2) Sprite.face[eid] = tx < 0 ? -1 : 1;
          if (Enemy.beat[eid] <= 0) {
            this.hexCast(eid, ex, ey, tx + pvx * 0.2, ty + pvy * 0.2);
            Enemy.mode[eid] = HEXER_KITE;
            Enemy.beat[eid] = c.castInterval * (0.8 + Enemy.seed[eid] * 0.45);
          }
        } else {
          const side = Enemy.seed[eid] < 0.5 ? -1 : 1;
          const band = dist - c.kiteRange;
          const radial = band > c.kiteSlack ? 1 : band < -c.kiteSlack ? -1.2 : (band / c.kiteSlack) * 0.4;
          const strafe = side * (0.75 + Math.sin(t * 0.7 + Enemy.seed[eid] * 9) * 0.25);
          this.separation(eid, ex, ey, Config.enemies.imp.separation);
          let sx = dx * radial - dy * strafe + this.sepX * 1.4;
          let sy = dy * radial + dx * strafe + this.sepY * 1.4;
          const slen = Math.hypot(sx, sy) || 1;
          const speed = c.speed * (stagger > 0 ? 0.35 : 1);
          desiredX = (sx / slen) * speed;
          desiredY = (sy / slen) * speed;
          if (stagger <= 0 && Enemy.beat[eid] <= 0 && dist < c.kiteRange + 4) {
            Enemy.mode[eid] = HEXER_WIND;
            Enemy.beat[eid] = c.telegraph;
            Sprite.flash[eid] = 0.5;
          }
        }
        Sprite.rot[eid] = Math.sin(t * 12 + Enemy.seed[eid] * 20) * 0.2;
      } else if (kind === EnemyKind.Cinder) {
        // Straight-line chase that speeds up the longer it lives; the burst is in DamageSystem.
        const c = Config.enemies.cinder;
        Enemy.beat[eid] += dt;
        const ramp = 1 + Math.min(1, Enemy.beat[eid] / c.rampSeconds) * (c.rampMax - 1);
        const wobble = Math.sin(t * 7 + Enemy.seed[eid] * 30) * 0.22;
        const speed = c.speed * ramp * (stagger > 0 ? 0.4 : 1);
        desiredX = (dx - dy * wobble) * speed;
        desiredY = (dy + dx * wobble) * speed;
        // Fuse glow: pulses faster the closer it gets.
        if (dist < 4.5) {
          const pulse = Math.sin(t * (10 + (4.5 - dist) * 5) + Enemy.seed[eid] * 6);
          if (pulse > 0.4) Sprite.flash[eid] = Math.max(Sprite.flash[eid], 0.25 + (4.5 - dist) * 0.08);
        }
        if (ctx.rng.chance(dt * 6)) emitParticles(ctx.fx, ParticleKind.Embers, ex, ey, -dx, -dy, 0.35, BRUTE_EMBER);
        Sprite.rot[eid] = Math.sin(t * 10 + Enemy.seed[eid] * 20) * 0.2;
      } else if (kind === EnemyKind.Mimic) {
        // Sleeps as a chest. Greed or a stray shot wakes it; then it hops hard and fast.
        const c = Config.enemies.mimic;
        const mode = Enemy.mode[eid];
        if (mode === MIMIC_DORMANT) {
          desiredX = 0;
          desiredY = 0;
          rate = 1;
          if (dist < c.wakeRange || Health.hp[eid] < Health.max[eid]) {
            Enemy.mode[eid] = MIMIC_WAKING;
            Enemy.beat[eid] = c.wakeSeconds;
            Sprite.pop[eid] = 0.8;
            Sprite.flash[eid] = 1;
            Sprite.squash[eid] = -0.6;
            Sprite.tint[eid] = 0xffb060;
            spawnFx(ctx, SpriteId.Shockwave, ex, ey, 0, 1.8, MIMIC_GOLD, 0.45);
            emitParticles(ctx.fx, ParticleKind.PickupSparkle, ex, ey, 0, 1, 1.2, MIMIC_GOLD);
            ctx.fx.shakeY += 0.25;
            ctx.fx.rumble = Math.max(ctx.fx.rumble, 0.5);
            ctx.events.push(GameEvent.MimicWake, eid, 0, ex, ey);
          }
        } else if (mode === MIMIC_WAKING) {
          Enemy.beat[eid] -= dt;
          desiredX = 0;
          desiredY = 0;
          rate = 1;
          Sprite.squash[eid] = Math.sin(t * 42) * 0.45;
          Sprite.flash[eid] = Math.max(Sprite.flash[eid], 0.3);
          if (Enemy.beat[eid] <= 0) {
            Enemy.mode[eid] = MIMIC_SQUAT;
            Enemy.beat[eid] = 0.12;
          }
        } else {
          Enemy.beat[eid] -= dt;
          if (Enemy.beat[eid] <= 0) {
            const hopping = mode === MIMIC_HOP;
            Enemy.mode[eid] = hopping ? MIMIC_SQUAT : MIMIC_HOP;
            Enemy.beat[eid] = hopping ? c.squatSeconds * (0.75 + Enemy.seed[eid] * 0.5) : c.hopSeconds;
            Sprite.squash[eid] = hopping ? 0.6 : -0.55;
            if (hopping) emitParticles(ctx.fx, ParticleKind.Dust, ex, ey - Radius.r[eid] * 0.6, 0, 1, 0.5, MIMIC_GOLD);
            else {
              const hx = tx + pvx * c.hopLead;
              const hy = ty + pvy * c.hopLead;
              const hlen = Math.hypot(hx, hy) || 1;
              Velocity.x[eid] = (hx / hlen) * c.hopSpeed;
              Velocity.y[eid] = (hy / hlen) * c.hopSpeed;
              if (Math.abs(hx) > 0.2) Sprite.face[eid] = hx < 0 ? -1 : 1;
            }
          }
          if (Enemy.mode[eid] === MIMIC_HOP && stagger <= 0) {
            desiredX = Velocity.x[eid];
            desiredY = Velocity.y[eid];
            rate = 1 - Math.exp(-16 * dt);
          } else {
            desiredX = 0;
            desiredY = 0;
          }
        }
        Sprite.rot[eid] = mode >= MIMIC_SQUAT ? Math.sin(t * 16 + Enemy.seed[eid] * 8) * 0.12 : 0;
      } else if (kind === EnemyKind.Golem) {
        const c = Config.enemies.golem;
        const speed = c.speed * (stagger > 0 ? Config.anim.golemStaggerSpeed : 1);
        Enemy.beat[eid] -= dt;
        if (stagger > 0 && Enemy.mode[eid] !== GOLEM_WALK) {
          Enemy.mode[eid] = GOLEM_WALK;
          Enemy.beat[eid] = 0.55;
        }
        if (Enemy.mode[eid] === GOLEM_STOMP) {
          desiredX = Velocity.x[eid];
          desiredY = Velocity.y[eid];
          if (Enemy.beat[eid] <= 0) {
            Enemy.mode[eid] = GOLEM_WALK;
            Enemy.beat[eid] = c.stompRest * (0.75 + Enemy.seed[eid] * 0.5);
          }
        } else if (Enemy.mode[eid] === GOLEM_WIND) {
          desiredX = 0;
          desiredY = 0;
          rate = 1 - Math.exp(-14 * dt);
          Sprite.squash[eid] = -0.55;
          Sprite.flash[eid] = Math.max(Sprite.flash[eid], 0.42);
          if (Enemy.beat[eid] <= 0) {
            Enemy.mode[eid] = GOLEM_STOMP;
            Enemy.beat[eid] = c.stompSeconds;
            Velocity.x[eid] = dx * c.stompSpeed;
            Velocity.y[eid] = dy * c.stompSpeed;
            Sprite.squash[eid] = 0.55;
            spawnFx(ctx, SpriteId.Shockwave, ex, ey, 0, 1.8, GOLEM_DUST, 0.45);
            emitParticles(ctx.fx, ParticleKind.Dust, ex, ey - 0.4, 0, 1, 1.1, GOLEM_DUST);
            emitParticles(ctx.fx, ParticleKind.GolemRubble, ex, ey, dx, dy, 0.55);
            ctx.fx.shakeY += 0.16;
          }
        } else {
          desiredX = dx * speed;
          desiredY = dy * speed;
          if (stagger <= 0 && Enemy.beat[eid] <= 0 && dist < c.stompRange) {
            Enemy.mode[eid] = GOLEM_WIND;
            Enemy.beat[eid] = c.stompWind;
            Sprite.flash[eid] = 0.55;
            Sprite.squash[eid] = -0.4;
            spawnFx(ctx, SpriteId.Shockwave, ex, ey, 0, 1.15, GOLEM_DUST, 0.4);
          } else if (Enemy.beat[eid] <= 0) {
            Enemy.beat[eid] = 0.2;
          }
          Sprite.rot[eid] = Math.sin(t * 6 + Enemy.seed[eid] * 10) * 0.08;
        }
      } else if (kind === EnemyKind.Skull) {
        // Hover in, then scream forward in a locked lunge aimed a beat ahead of the wizard.
        const c = Config.enemies.skull;
        Enemy.beat[eid] -= dt;
        if (Enemy.beat[eid] <= 0) {
          const lunging = Enemy.mode[eid] === 1;
          Enemy.mode[eid] = lunging ? 0 : 1;
          Enemy.beat[eid] = lunging ? c.restSeconds * (0.75 + Enemy.seed[eid] * 0.35) : c.lungeSeconds;
          if (!lunging) {
            const lx = tx + pvx * c.lead;
            const ly = ty + pvy * c.lead;
            const llen = Math.max(0.05, Math.hypot(lx, ly));
            Velocity.x[eid] = (lx / llen) * c.lungeSpeed;
            Velocity.y[eid] = (ly / llen) * c.lungeSpeed;
            Sprite.squash[eid] = -0.5;
          }
        }
        if (Enemy.mode[eid] === 1) {
          desiredX = Velocity.x[eid];
          desiredY = Velocity.y[eid];
        } else {
          const wobble = Math.sin(t * 5 + Enemy.seed[eid] * 30) * 0.6;
          desiredX = (dx - dy * wobble) * c.speed;
          desiredY = (dy + dx * wobble) * c.speed;
        }
        if (stagger > 0) {
          desiredX *= 0.3;
          desiredY *= 0.3;
        }
        Sprite.rot[eid] = Math.sin(t * 8 + Enemy.seed[eid] * 20) * 0.18;
      } else if (kind === EnemyKind.Slime || kind === EnemyKind.Blight) {
        // Squat, then a locked hop aimed a little ahead of the wizard. Blights: same beat, heavier.
        const blight = kind === EnemyKind.Blight;
        const c = blight ? Config.enemies.blight : Config.enemies.slime;
        Enemy.beat[eid] -= dt;
        if (Enemy.beat[eid] <= 0) {
          const hopping = Enemy.mode[eid] === 1;
          Enemy.mode[eid] = hopping ? 0 : 1;
          Enemy.beat[eid] = hopping ? c.squatSeconds * (0.7 + Enemy.seed[eid] * 0.6) : c.hopSeconds;
          Sprite.squash[eid] = hopping ? 0.7 : -0.6;
          if (hopping) {
            emitParticles(ctx.fx, ParticleKind.ImpGoo, ex, ey - Radius.r[eid] * 0.6, 0, 1, blight ? 0.5 : 0.25, blight ? BLIGHT_GOO : SLIME_GOO);
            if (blight) ctx.fx.shakeY += 0.05;
          } else {
            this.separation(eid, ex, ey, c.separation);
            const hx = tx + pvx * c.hopLead + this.sepX * 1.2;
            const hy = ty + pvy * c.hopLead + this.sepY * 1.2;
            const hlen = Math.hypot(hx, hy) || 1;
            Velocity.x[eid] = (hx / hlen) * c.hopSpeed;
            Velocity.y[eid] = (hy / hlen) * c.hopSpeed;
          }
        }
        if (Enemy.mode[eid] === 1 && stagger <= 0) {
          desiredX = Velocity.x[eid];
          desiredY = Velocity.y[eid];
          rate = 1 - Math.exp(-16 * dt);
        } else {
          desiredX = 0;
          desiredY = 0;
        }
        Sprite.rot[eid] = 0;
      } else if (kind === EnemyKind.Bat) {
        // Each bat owns its own ring, then dives through an offset near the wizard.
        const c = Config.enemies.bat;
        const orbit = c.orbitRadius * (0.68 + Enemy.seed[eid] * 0.7);
        Enemy.beat[eid] -= dt;
        if (Enemy.beat[eid] <= 0) {
          const diving = Enemy.mode[eid] === 1;
          Enemy.mode[eid] = diving ? 0 : 1;
          Enemy.beat[eid] = diving ? c.orbitSeconds * (0.65 + Enemy.seed[eid] * 0.7) : c.diveSeconds;
          if (!diving) {
            const offAng = Enemy.seed[eid] * Math.PI * 2;
            const aimX = tx + Math.cos(offAng) * c.diveOffset + pvx * c.diveLead;
            const aimY = ty + Math.sin(offAng) * c.diveOffset + pvy * c.diveLead;
            const alen = Math.max(0.05, Math.hypot(aimX, aimY));
            Velocity.x[eid] = (aimX / alen) * c.diveSpeed;
            Velocity.y[eid] = (aimY / alen) * c.diveSpeed;
            Sprite.squash[eid] = -0.4;
          }
        }
        if (Enemy.mode[eid] === 1) {
          desiredX = Velocity.x[eid];
          desiredY = Velocity.y[eid];
        } else {
          const side = Enemy.seed[eid] < 0.5 ? -1 : 1;
          const radial = (dist - orbit) / orbit;
          const rx = dx * radial * 1.5 - dy * side;
          const ry = dy * radial * 1.5 + dx * side;
          const len = Math.hypot(rx, ry) || 1;
          const speed = c.speed * (0.88 + Enemy.seed[eid] * 0.24);
          desiredX = (rx / len) * speed;
          desiredY = (ry / len) * speed;
        }
        if (stagger > 0) {
          desiredX *= 0.4;
          desiredY *= 0.4;
        }
        Sprite.rot[eid] = Math.sin(t * 11 + Enemy.seed[eid] * 20) * 0.12;
      } else {
        // Imp: peel around an owned ring, occasional sidestep fake, then a telegraphed lunge.
        const c = Config.enemies.imp;
        Enemy.beat[eid] -= dt;
        const mode = Enemy.mode[eid];
        if (stagger > 0 && mode !== IMP_CHASE) {
          Enemy.mode[eid] = IMP_CHASE;
          Enemy.beat[eid] = 0.35;
        }
        if (Enemy.mode[eid] === IMP_LUNGE) {
          desiredX = Velocity.x[eid];
          desiredY = Velocity.y[eid];
          if (Enemy.beat[eid] <= 0) {
            Enemy.mode[eid] = IMP_CHASE;
            Enemy.beat[eid] = 0.45 + Enemy.seed[eid] * 0.35;
          }
        } else if (Enemy.mode[eid] === IMP_WIND) {
          desiredX = dx * c.speed * 0.22;
          desiredY = dy * c.speed * 0.22;
          Sprite.squash[eid] = -0.48;
          Sprite.flash[eid] = Math.max(Sprite.flash[eid], 0.4);
          if (Enemy.beat[eid] <= 0) {
            Enemy.mode[eid] = IMP_LUNGE;
            Enemy.beat[eid] = c.lungeSeconds;
            Velocity.x[eid] = dx * c.lungeSpeed;
            Velocity.y[eid] = dy * c.lungeSpeed;
            Sprite.squash[eid] = -0.5;
          }
        } else if (Enemy.mode[eid] === IMP_FAKE) {
          const side = Enemy.seed[eid] < 0.5 ? -1 : 1;
          this.separation(eid, ex, ey, c.separation);
          let sx = -dy * side + this.sepX * 1.2;
          let sy = dx * side + this.sepY * 1.2;
          const slen = Math.hypot(sx, sy) || 1;
          desiredX = (sx / slen) * c.speed * 1.15;
          desiredY = (sy / slen) * c.speed * 1.15;
          if (Enemy.beat[eid] <= 0) {
            Enemy.mode[eid] = IMP_CHASE;
            Enemy.beat[eid] = 0.18 + Enemy.seed[eid] * 0.22;
          }
        } else {
          const side = Enemy.seed[eid] < 0.5 ? -1 : 1;
          const closeness = Math.max(0, 1 - dist / 7);
          const tang = c.encircle * (0.5 + closeness * 1.05) * side;
          let sx = dx - dy * tang;
          let sy = dy + dx * tang;
          this.separation(eid, ex, ey, c.separation);
          sx += this.sepX * 1.6;
          sy += this.sepY * 1.6;
          const len = Math.hypot(sx, sy) || 1;
          dx = sx / len;
          dy = sy / len;
          const jitter = 1 + Math.sin(t * 9 + Enemy.seed[eid] * 40) * 0.12;
          const speed = c.speed * jitter * (stagger > 0 ? 0.35 : 1) * (1 + (ctx.room.tier - 1) * 0.06);
          desiredX = dx * speed;
          desiredY = dy * speed;
          if (stagger <= 0 && Enemy.beat[eid] <= 0) {
            const trick = (Enemy.seed[eid] + t * 0.17) % 1;
            if (dist < 1.9) {
              Enemy.mode[eid] = IMP_WIND;
              Enemy.beat[eid] = c.windSeconds;
              Sprite.flash[eid] = 0.5;
            } else if (dist < 4.6 && trick < 0.4) {
              Enemy.mode[eid] = IMP_FAKE;
              Enemy.beat[eid] = c.sidestepSeconds * (0.75 + Enemy.seed[eid] * 0.5);
            } else {
              Enemy.beat[eid] = 0.26 + Enemy.seed[eid] * 0.4;
            }
          }
        }
        Sprite.rot[eid] = Math.sin(t * 14 + Enemy.seed[eid] * 20) * 0.25;
      }

      Velocity.x[eid] += (desiredX * haste - Velocity.x[eid]) * rate;
      Velocity.y[eid] += (desiredY * haste - Velocity.y[eid]) * rate;
      this.crowdSlide(eid, ex, ey);
    }
  }

  /** Hexer release: one aimed bolt, or a narrow twin fan on late tiers. */
  private hexCast(eid: number, ex: number, ey: number, aimX: number, aimY: number): void {
    const ctx = this.ctx;
    const c = Config.enemies.hexer;
    const len = Math.hypot(aimX, aimY) || 1;
    const base = Math.atan2(aimY / len, aimX / len);
    const twin = ctx.room.tier >= c.twinFromTier;
    const speed = c.boltSpeed * difficulty().projectileSpeed;
    const shots = twin ? 2 : 1;
    for (let s = 0; s < shots; s++) {
      const a = base + (twin ? (s === 0 ? -1 : 1) * 5 * (Math.PI / 180) : 0);
      const ax = Math.cos(a);
      const ay = Math.sin(a);
      this.hexBolt.owner = eid;
      this.hexBolt.x = ex + ax * 0.45;
      this.hexBolt.y = ey + ay * 0.45 + 0.2;
      this.hexBolt.vx = ax * speed;
      this.hexBolt.vy = ay * speed;
      spawnProjectile(ctx, this.hexBolt);
    }
    spawnFx(ctx, SpriteId.MuzzleWand, ex + Math.cos(base) * 0.5, ey + Math.sin(base) * 0.5 + 0.2, base, 0.7, HEXER_BOLT, 0.82);
    emitParticles(ctx.fx, ParticleKind.MuzzleSparks, ex, ey, Math.cos(base), Math.sin(base), 0.5, HEXER_BOLT);
    Sprite.squash[eid] = 0.4;
    Sprite.joltX[eid] = -Math.cos(base) * 0.15;
    Sprite.joltY[eid] = -Math.sin(base) * 0.15;
  }

  /**
   * Boids-style separation: sum of unit pushes away from living bodies inside
   * `range` (wu, centre to centre; must not exceed the gathered radius), each
   * weighted by how deep inside it sits. Result lands in sepX/sepY (unnormalised).
   */
  private separation(eid: number, ex: number, ey: number, range: number): void {
    const n = this.neighborCount;
    let sepX = 0;
    let sepY = 0;
    for (let k = 0; k < n; k++) {
      const other = this.neighbors[k];
      if (other === eid || Dying.flag[other]) continue;
      const ox = ex - Position.x[other];
      const oy = ey - Position.y[other];
      const d2 = ox * ox + oy * oy;
      if (d2 > range * range) continue;
      if (d2 < COINCIDENT_EPS2) {
        const angle = eid * 2.3999632 + other;
        sepX += Math.cos(angle);
        sepY += Math.sin(angle);
        continue;
      }
      const d = Math.sqrt(d2);
      const push = (range - d) / range;
      sepX += (ox / d) * push;
      sepY += (oy / d) * push;
    }
    this.sepX = sepX;
    this.sepY = sepY;
  }

  /**
   * Keep tangent speed, drop the component walking into a neighbor. Golems skip
   * this so they still plow; collision gives them the mass to shove small fry out.
   */
  private crowdSlide(eid: number, ex: number, ey: number): void {
    const kind = Enemy.kind[eid];
    if (kind === EnemyKind.Golem || kind === EnemyKind.Brute || isWallEnemy(kind)) return;

    const c = Config.collision;
    const aR = bodyRadius(eid);
    const n = this.neighborCount;
    let vx = Velocity.x[eid];
    let vy = Velocity.y[eid];

    for (let k = 0; k < n; k++) {
      const other = this.neighbors[k];
      if (other === eid || Dying.flag[other]) continue;
      const dx = Position.x[other] - ex;
      const dy = Position.y[other] - ey;
      const d2 = dx * dx + dy * dy;
      const minDist = enemyPairMinDist(kind, aR, Enemy.kind[other], bodyRadius(other)) + c.slop + c.crowdPad;
      if (d2 > minDist * minDist) continue;

      let nx: number;
      let ny: number;
      if (d2 < COINCIDENT_EPS2) {
        const angle = eid * 2.3999632 + other;
        nx = Math.cos(angle);
        ny = Math.sin(angle);
      } else {
        const dist = Math.sqrt(d2);
        nx = dx / dist;
        ny = dy / dist;
      }
      const inward = vx * nx + vy * ny;
      if (inward <= 0) continue;
      vx -= nx * inward * c.crowdSlide;
      vy -= ny * inward * c.crowdSlide;
    }

    Velocity.x[eid] = vx;
    Velocity.y[eid] = vy;
  }
}
